import { buildAuthHeaders } from "../forum/authHeaders";

const IS_LOCAL_FRONTEND =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE_URL = IS_LOCAL_FRONTEND
  ? process.env.REACT_APP_API_BASE_URL_LOCAL || ""
  : process.env.REACT_APP_API_BASE_URL || "";

function apiUrl(path) {
  return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

export const WALLET_ENDPOINTS = Object.freeze({
  summary: "/api/wallet/summary/",
  capabilities: "/api/wallet/capabilities/",
  transactions: "/api/wallet/transactions/",
  pending: "/api/wallet/pending/",
  notifications: "/api/wallet/notifications/",
  coupons: "/api/wallet/coupons/",
  couponApply: "/api/wallet/coupons/apply/",
  preferences: "/api/wallet/preferences/",
});

const FALSE_CAPABILITIES = Object.freeze({
  walletEnabled: false,
  realMoneyEnabled: false,
  depositEnabled: false,
  withdrawalEnabled: false,
  couponsEnabled: false,
  pointsEnabled: false,
  pointsRedemptionEnabled: false,
  bonusEnabled: false,
  marketplacePaymentsEnabled: false,
  crowdfundingPaymentsEnabled: false,
});

function firstDefined(source, keys) {
  for (const key of keys) {
    if (source?.[key] !== undefined) return source[key];
  }
  return undefined;
}

function booleanCapability(source, keys) {
  return firstDefined(source, keys) === true;
}

export function normalizeWalletCapabilities(payload) {
  const source = payload?.capabilities && typeof payload.capabilities === "object"
    ? payload.capabilities
    : payload && typeof payload === "object"
      ? payload
      : {};

  return {
    ...FALSE_CAPABILITIES,
    walletEnabled: booleanCapability(source, ["wallet_enabled", "walletEnabled", "enabled"]),
    realMoneyEnabled: booleanCapability(source, ["real_money_enabled", "realMoneyEnabled"]),
    depositEnabled: booleanCapability(source, ["deposit_enabled", "depositEnabled"]),
    withdrawalEnabled: booleanCapability(source, [
      "withdrawal_enabled",
      "withdraw_enabled",
      "withdrawalEnabled",
      "withdrawEnabled",
    ]),
    couponsEnabled: booleanCapability(source, ["coupons_enabled", "couponsEnabled"]),
    pointsEnabled: booleanCapability(source, ["points_enabled", "pointsEnabled"]),
    pointsRedemptionEnabled: booleanCapability(source, [
      "points_redemption_enabled",
      "pointsRedemptionEnabled",
      "use_points_enabled",
    ]),
    bonusEnabled: booleanCapability(source, ["bonus_enabled", "bonusEnabled"]),
    marketplacePaymentsEnabled: booleanCapability(source, [
      "marketplace_payments_enabled",
      "marketplacePaymentsEnabled",
    ]),
    crowdfundingPaymentsEnabled: booleanCapability(source, [
      "crowdfunding_payments_enabled",
      "crowdfundingPaymentsEnabled",
    ]),
  };
}

export function normalizeWalletPreferences(payload) {
  const source = payload?.preferences && typeof payload.preferences === "object"
    ? payload.preferences
    : payload && typeof payload === "object"
      ? payload
      : {};
  return {
    hideBalances: source.hide_balances === true || source.hideBalances === true,
    showHeaderBalance: source.show_header_balance !== false && source.showHeaderBalance !== false,
  };
}

export function decimalStringToMinor(value) {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    value = String(value);
  }
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(",", ".");
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(normalized);
  if (!match) return null;
  const sign = match[1] === "-" ? -1 : 1;
  const whole = Number(match[2]);
  const fraction = Number((match[3] || "").padEnd(2, "0"));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(fraction)) return null;
  const minor = sign * (whole * 100 + fraction);
  return Number.isSafeInteger(minor) ? minor : null;
}

function integerOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function minorFrom(source, minorKeys, decimalKeys = []) {
  const direct = integerOrNull(firstDefined(source, minorKeys));
  if (direct !== null) return direct;
  const legacy = firstDefined(source, decimalKeys);
  return decimalStringToMinor(legacy);
}

function collection(payload, { required = false } = {}) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (required) throw new Error("Collection portefeuille invalide.");
  return [];
}

function normalizedStatus(value, fallback = "recorded") {
  return String(value || fallback).trim().toLowerCase();
}

export function normalizeWalletTransaction(item, index = 0) {
  const amountMinor = minorFrom(item, ["amount_minor", "amountMinor"], ["amount"]);
  const pointsDelta = integerOrNull(firstDefined(item, ["points_delta", "pointsDelta", "points"]));
  return {
    id: String(item?.id ?? item?.public_id ?? `transaction-${index}`),
    label: String(item?.label || item?.title || item?.description || "Opération portefeuille"),
    type: String(item?.type || item?.transaction_type || item?.operation_type || "operation"),
    assetCode: String(item?.asset_code || item?.assetCode || ""),
    direction: String(item?.direction || "").toLowerCase(),
    method: String(item?.method || ""),
    status: normalizedStatus(item?.status),
    amountMinor,
    pointsDelta,
    currency: String(item?.currency || "EUR").toUpperCase(),
    sourceLabel: String(item?.source_label || item?.sourceLabel || item?.source_type || ""),
    sourceType: String(item?.source_type || item?.sourceType || ""),
    sourceUrl: String(item?.source_url || item?.sourceUrl || item?.deep_link || ""),
    createdAt: item?.created_at || item?.createdAt || null,
    effectiveAt: item?.effective_at || item?.effectiveAt || null,
    balanceAfterMinor: minorFrom(
      item,
      ["balance_after_minor", "balanceAfterMinor"],
      ["balance_after"]
    ),
    pointsAfter: integerOrNull(firstDefined(item, ["points_after", "pointsAfter", "balance_after_points"])),
    reference: String(item?.reference || item?.correlation_id || ""),
  };
}

function normalizeWalletLedgerEntry(item, index = 0) {
  return {
    id: String(item?.id ?? `entry-${index}`),
    accountId: String(item?.account_id || ""),
    accountKind: String(item?.account_kind || ""),
    direction: String(item?.direction || "").toLowerCase(),
    assetCode: String(item?.asset_code || ""),
    amountMinor: integerOrNull(item?.amount_minor),
    balanceAfter: integerOrNull(item?.balance_after),
    effectiveAt: item?.effective_at || null,
  };
}

export function normalizeWalletTransactionDetail(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Détail de transaction invalide.");
  }
  return {
    ...normalizeWalletTransaction(payload),
    entries: collection(payload.entries).map(normalizeWalletLedgerEntry),
  };
}

export function normalizeWalletPending(item, index = 0) {
  const amountMinor = minorFrom(
    item,
    ["amount_minor", "amountMinor", "pending_minor", "pendingMinor"],
    ["amount"]
  );
  return {
    id: String(item?.id ?? item?.public_id ?? `pending-${index}`),
    label: String(item?.label || item?.title || "Opération en cours"),
    status: normalizedStatus(item?.status, "pending"),
    amountMinor,
    currency: String(item?.currency || "EUR").toUpperCase(),
    direction: String(item?.direction || item?.role || ""),
    sourceLabel: String(item?.source_label || item?.sourceLabel || item?.source_type || ""),
    sourceUrl: String(item?.source_url || item?.sourceUrl || item?.deep_link || ""),
    updatedAt: item?.updated_at || item?.updatedAt || item?.created_at || null,
  };
}

export function normalizeWalletCoupon(item, index = 0) {
  return {
    id: String(item?.id ?? item?.public_id ?? item?.code ?? `coupon-${index}`),
    code: String(item?.code || "").toUpperCase(),
    label: String(item?.label || item?.title || "Coupon Eternua"),
    status: normalizedStatus(item?.status, item?.used_at ? "used" : "available"),
    bonusMinor: minorFrom(item, ["bonus_minor", "amount_minor", "bonusMinor"], ["bonus", "amount"]),
    currency: String(item?.currency || "EUR").toUpperCase(),
    usedAt: item?.used_at || item?.usedAt || null,
    expiresAt: item?.expires_at || item?.expiresAt || null,
  };
}

export function normalizeWalletNotification(item, index = 0) {
  return {
    id: String(item?.id ?? item?.event_id ?? `wallet-notification-${index}`),
    eventId: String(item?.event_id || item?.id || ""),
    eventType: String(item?.event_type || "wallet.event"),
    title: String(item?.title || "Mise à jour du portefeuille"),
    message: String(item?.message || "Une opération portefeuille a été enregistrée."),
    status: normalizedStatus(item?.status),
    sourceLabel: String(item?.source_label || "Portefeuille Eternua"),
    sourceUrl: String(item?.source_url || ""),
    transactionId: String(item?.transaction_id || ""),
    createdAt: item?.created_at || null,
  };
}

export function normalizeWalletSummary(payload) {
  const source = payload?.summary && typeof payload.summary === "object"
    ? { ...payload, ...payload.summary }
    : payload;
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    throw new Error("Résumé portefeuille invalide.");
  }

  const balanceMinor = minorFrom(source, ["balance_minor", "balanceMinor"], ["balance"]);
  const bonusMinor = minorFrom(source, ["bonus_minor", "bonusMinor"], ["bonus"]);
  if (balanceMinor === null || bonusMinor === null) {
    throw new Error("Résumé portefeuille invalide : montants officiels absents.");
  }

  const points = integerOrNull(firstDefined(source, ["points", "reward_points", "points_balance"]));
  const pendingValue = firstDefined(source, ["pending_minor", "pendingMinor"]);
  const pendingMinor = pendingValue === null ? null : integerOrNull(pendingValue);

  return {
    walletId: String(source.wallet_id || source.walletId || source.id || ""),
    currency: String(source.currency || "EUR").toUpperCase(),
    balanceMinor,
    withdrawableMinor: null, // External withdrawal is not connected; never derive it from the ledger.
    reservedMinor: integerOrNull(source.pending_reserved_minor),
    unsettledMinor: integerOrNull(source.pending_unsettled_minor),
    bonusMinor,
    points,
    pendingMinor,
    pendingSource: source.pending_source || source.pendingSource || null,
    updatedAt: source.updated_at || source.updatedAt || null,
    capabilities: normalizeWalletCapabilities(source.capabilities || payload?.capabilities),
    preferences: normalizeWalletPreferences(source.preferences || payload?.preferences),
    transactions: collection(source.transactions).map(normalizeWalletTransaction),
    pending: collection(source.pending).map(normalizeWalletPending),
    coupons: collection(source.coupons).map(normalizeWalletCoupon),
  };
}

function publicErrorMessage(payload, fallback) {
  if (typeof payload === "string" && payload.trim()) return payload.trim();
  if (payload && typeof payload === "object") {
    return String(payload.detail || payload.message || payload.error || fallback);
  }
  return fallback;
}

async function readPayload(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_error) {
    return text;
  }
}

async function requestJson(path, { signal, method = "GET", body } = {}) {
  const response = await fetch(apiUrl(path), {
    method,
    cache: "no-store",
    credentials: "same-origin",
    headers: buildAuthHeaders({
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    }),
    signal,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await readPayload(response);
  if (!response.ok) {
    const error = new Error(
      publicErrorMessage(payload, `Service portefeuille indisponible (${response.status}).`)
    );
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

export async function fetchWalletSummary({ signal } = {}) {
  return normalizeWalletSummary(await requestJson(WALLET_ENDPOINTS.summary, { signal }));
}

const SECTION_CONFIG = Object.freeze({
  transactions: {
    endpoint: WALLET_ENDPOINTS.transactions,
    normalize: normalizeWalletTransaction,
  },
  pending: {
    endpoint: WALLET_ENDPOINTS.pending,
    normalize: normalizeWalletPending,
  },
  coupons: {
    endpoint: WALLET_ENDPOINTS.coupons,
    normalize: normalizeWalletCoupon,
  },
  notifications: {
    endpoint: WALLET_ENDPOINTS.notifications,
    normalize: normalizeWalletNotification,
  },
});

export async function fetchWalletSection(section, { signal, page = 1 } = {}) {
  const config = SECTION_CONFIG[section];
  if (!config) throw new Error(`Section portefeuille inconnue : ${section}`);
  const url = `${config.endpoint}?page=${Math.max(1, Number(page) || 1)}`;
  const payload = await requestJson(url, { signal });
  const rows = collection(payload, { required: true });
  return {
    results: rows.map(config.normalize),
    count: integerOrNull(payload?.count) ?? rows.length,
    next: payload?.next || null,
    previous: payload?.previous || null,
  };
}

export async function fetchWalletTransactionDetail(transactionId, { signal } = {}) {
  const opaqueId = String(transactionId || "").trim();
  if (!opaqueId) throw new Error("Identifiant de transaction manquant.");
  const payload = await requestJson(
    `${WALLET_ENDPOINTS.transactions}${encodeURIComponent(opaqueId)}/`,
    { signal }
  );
  return normalizeWalletTransactionDetail(payload);
}

export async function fetchWalletCapabilities({ signal } = {}) {
  return normalizeWalletCapabilities(
    await requestJson(WALLET_ENDPOINTS.capabilities, { signal })
  );
}

export async function applyWalletCoupon(code, { signal } = {}) {
  const normalizedCode = String(code || "").trim().toUpperCase();
  if (!normalizedCode) throw new Error("Saisis un code coupon.");
  return requestJson(WALLET_ENDPOINTS.couponApply, {
    signal,
    method: "POST",
    body: { code: normalizedCode },
  });
}

export async function updateWalletPreferences(preferences, { signal } = {}) {
  const body = {};
  if (preferences?.hideBalances !== undefined) {
    body.hide_balances = preferences.hideBalances === true;
  }
  if (preferences?.showHeaderBalance !== undefined) {
    body.show_header_balance = preferences.showHeaderBalance === true;
  }
  if (Object.keys(body).length === 0) throw new Error("Aucune préférence à modifier.");
  const payload = await requestJson(WALLET_ENDPOINTS.preferences, {
    signal,
    method: "PATCH",
    body,
  });
  return normalizeWalletPreferences(payload);
}
